import Box from "@mui/material/Box";
import FilterIcon from '@mui/icons-material/Filter';
import Filter1Icon from '@mui/icons-material/Filter1';
import Filter2Icon from '@mui/icons-material/Filter2';
import Filter3Icon from '@mui/icons-material/Filter3';
import Filter4Icon from '@mui/icons-material/Filter4';
import Filter5Icon from '@mui/icons-material/Filter5';
import Filter6Icon from '@mui/icons-material/Filter6';
import Filter7Icon from '@mui/icons-material/Filter7';
import Filter8Icon from '@mui/icons-material/Filter8';
import Filter9PlusIcon from '@mui/icons-material/Filter9Plus';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Button, List, Tooltip } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Icon from '@mui/material/Icon';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ActionCategoryAssociations from '../defaults/ActionCategoryAssociations.json';
import ColorConstants from '../defaults/ColorConstants';
import FieldNames from '../defaults/FieldNames.json';
import { setDefinitionExpression } from '../redux/slices/filterSlice';
import ErrorService from '../services/ErrorService';
import FeatureService from '../services/FeatureService';
import FilterPopulationService from '../services/FilterPopulationService';
import CustomFilterList from './arcgisFilter/components/CustomFilterList';
import FiltersContainer from './filter/FiltersContainer/FiltersContainer';
import "./list/TreatmentActivityList/TreatmentActivityList.css";
import "./FilterPanel.css"
import { setWbsIDValues, setCongressDistValues, setActionCategoryAssociations, setFilterActionsDisabled } from "../redux/slices/globalContextSlice";

export default function FilterPanel({setIsBasicFilterActive, setIsCustomFilterActive, 
  isCreatingActualActivity, isCreatingActualTreatment, isCreatingPlannedTreatment, 
  isCreatingPlannedActivity }) {

  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
  const dispatch = useDispatch();
  const activeFilterCountValue = useSelector((state) => state.filter.activeFilterCount);
  const isCustomFilterActive = useSelector((state) => state.filter.isCustomFilterActive);
  const [showBasic, setShowBasic] = useState(isCustomFilterActive ? false : true); 
  const [showCustom, setShowCustom] = useState(isCustomFilterActive ? true : false);
  const [filterSelection,setFilterSelection] = useState(isCustomFilterActive ? "CUSTOM" : "BASIC");
  const isViewingActions = useSelector((state) => state.menu.viewingActions);
  const isViewingProjects = useSelector((state) => state.menu.viewingProjects);
  const definitionExpression = useSelector((state) => state.filter.definitionExpression);
  const filterExpression = useSelector((state) => state.filter.filterExpression);
  const filterActionsDisabled = useSelector((state) => state.globalContext.filterActionsDisabled);

  const wbsIDValues = useSelector((state) => state.globalContext.wbsIDValues);
  const congressDistValues = useSelector((state) => state.globalContext.congressDistValues);
  const actionCategoryAssociations = useSelector((state) => state.globalContext.actionCategoryAssociations);


  useEffect(() => {
    if(!congressDistValues || (congressDistValues && congressDistValues.length === 0)){
      FilterPopulationService.getCongressionalDistNames(config).then((congDistNames) => {
        dispatch(setCongressDistValues(congDistNames));
      }).catch(error => {
        ErrorService.addErrorLogEntry(error);
      });
    }
  }, [config, congressDistValues])
  useEffect(() => {
    setShowBasic(!isCustomFilterActive);
    setShowCustom(isCustomFilterActive);
    setFilterSelection(isCustomFilterActive ? "CUSTOM" : "BASIC");
  }, [isCustomFilterActive]);

  const placeForDispatch = useCallback(args => {
    dispatch(setIsBasicFilterActive(isCustomFilterActive ? false : true));
    dispatch(setIsCustomFilterActive(isCustomFilterActive ? true : false));
  }, [dispatch, setIsBasicFilterActive, setIsCustomFilterActive])

  useEffect(() => {
    placeForDispatch();
  }, [placeForDispatch]);

  useEffect(() => {
    if(!rootServiceUrl || !config || (wbsIDValues && wbsIDValues.length > 0)){
      return;
    }

    if(!actionCategoryAssociations?.length){
      const catTypes = ActionCategoryAssociations;
      dispatch(setActionCategoryAssociations(catTypes));
    }

    (async () => {
      const allWbsIds = await FeatureService.getWBSIDValues().then(response => {
        if(response && response.length > 0){
          return response;
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(error);
      });


      const allWithoutDuplicates = [...(allWbsIds ? new Set(allWbsIds) : [])];
      dispatch(setWbsIDValues(allWithoutDuplicates));
    })();
  }, [config, rootServiceUrl]);

  const handleRadioFilterChange = () => {
    if(filterSelection === "BASIC")
    {
      handleCustom();
    }
    else
    {
      handleBasic();
    }
  }

  const handleBasic = () => {
    setFilterSelection("BASIC");
    setShowBasic(!showBasic);
    setShowCustom(false);
    dispatch(setIsBasicFilterActive(true));
    dispatch(setIsCustomFilterActive(false));
  }

  const handleCustom = () => {
    setFilterSelection("CUSTOM");
    setShowCustom(!showCustom);
    setShowBasic(false);
    dispatch(setIsBasicFilterActive(false));
    dispatch(setIsCustomFilterActive(true));
  }

  const renderFilterIcon = (activeFilterCount, isCustomFilter) => {
    if(isCustomFilter)
    {
      return <FilterIcon></FilterIcon>;
    }
    if(activeFilterCount > 8)
    {
      return <Filter9PlusIcon></Filter9PlusIcon>
    }
    switch(activeFilterCount) {
      case 1:
        return <Filter1Icon></Filter1Icon>;
      case 2:
        return <Filter2Icon></Filter2Icon>;  
      case 3:
        return <Filter3Icon></Filter3Icon>;  
      case 4:
        return <Filter4Icon></Filter4Icon>;  
      case 5:
        return <Filter5Icon></Filter5Icon>;  
      case 6:
        return <Filter6Icon></Filter6Icon>;  
      case 7:
        return <Filter7Icon></Filter7Icon>;  
      case 8:
        return <Filter8Icon></Filter8Icon>;  
      default:
        return <Fragment></Fragment>;
    }
  }

  const applyFilters = () => {
    dispatch(setDefinitionExpression(filterExpression));
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      { filterActionsDisabled ? 
        <div className="filter-disabled-overlay">
          <div className="filter-disabled-content">
            <WarningAmberIcon style={{ color: ColorConstants.StatusColors.Draft, fontSize: 60}} />
            <span style={{ marginTop: 16, fontSize: 18, color: ColorConstants.TextPrimary}}>
              Filter updates are temporarily disabled while editing. Please save or cancel your changes to re-enable filters.
            </span>
          </div>
        </div>
       : null  
      }
      <div className={`incidentList-filter ${filterActionsDisabled ? 'isDisabled' : ''}`}>
        <div className='incidentList-buttons'>
          <div className="panel-header">
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="filter-controlled-radio-buttons-group"
                name="filter-controlled-radio-buttons-group"
                value={filterSelection}
                onChange={handleRadioFilterChange}
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                className='header-radio-group'
              >
                <div>
                  <FormControlLabel disabled={isViewingProjects} value="BASIC" control={<Radio />} label="Basic" />
                  <FormControlLabel disabled={isViewingProjects} value="CUSTOM" control={<Radio />} label="Custom" />
                </div>
                <div style={{ display: 'flex'}}>
                  <span style={{ height: 40}}>
                  { definitionExpression !== filterExpression &&
                    <Tooltip title="The Apply button must be clicked to apply filters" enterDelay={400}>
                      <WarningAmberIcon style={{ color: ColorConstants.StatusColors.Draft, fontSize: 40}} />
                    </Tooltip>
                  }
                  </span>
                  <Button 
                    className='apoply-filters-button'
                    color="primary" 
                    variant="contained" 
                    disabled={isViewingProjects}
                    onClick={() => applyFilters()}>
                    Apply
                  </Button>
                  <Icon 
                    style={{
                      color: ColorConstants.AvatarColor,
                      borderRadius: "0%",
                      width: "36px",
                      height: "36px",
                      padding: "6px",
                    }}
                  >
                    {
                      renderFilterIcon(activeFilterCountValue, showCustom)
                    }
                  </Icon>
                </div>
              </RadioGroup>
            </FormControl>
          </div>
        </div>
        <div className="incident-list">
          <div className={"incident-list-inner"}>
            <List>
            {showBasic && isViewingActions &&
              <div className="filter-container-class" style={{ height: '100%' }}>
                <FiltersContainer 
                  congressDistValues={congressDistValues}
                  isCreatingActualActivity={isCreatingActualActivity} 
                  isCreatingActualTreatment={isCreatingActualTreatment} 
                  isCreatingPlannedTreatment={isCreatingPlannedTreatment} 
                  isCreatingPlannedActivity={isCreatingPlannedActivity} 
                  wbsIDValues={wbsIDValues}
                  rootServiceUrl={rootServiceUrl}
                  config={config}
                />
              </div>
            }
            {showCustom && isViewingActions &&
              <div className="filter-container-class" style={{ height: '100%' }}>
                <CustomFilterList 
                  congressDistValues={congressDistValues} 
                  wbsIDValues={wbsIDValues}
                />
              </div>
            }
            </List>
          </div>
        </div>
      </div>
    </Box>
  );
}